/**
 * Memo WRITE endpoints — request schemas (Pattern B) and the audio path rule.
 *
 * These schemas ARE THE FIELD WHITELIST for `public.memos`. All three are
 * `.strict()` on purpose: zod silently STRIPS unknown keys otherwise, so a
 * body carrying `organization_id` or `transcription_status` would 200 with the
 * key quietly dropped instead of 400ing, and the whitelist would be
 * untestable.
 *
 * Memo writes are backend-only once the lockdown migration lands
 * (`*_memos_backend_write_lockdown.sql`: RESTRICTIVE `verify_backend_token()`
 * on INSERT/UPDATE/DELETE). The handlers in `apps/api/src/routes/platform/memos.ts`
 * write through the per-request user client, which signs `x-backend-auth`.
 *
 * NEVER ACCEPTED (by any schema here):
 *   - `organization_id`  — from the validated `:orgId` route param.
 *   - `created_by`       — from the verified token (`c.get("userId")`); the
 *                          INSERT policy's `WITH CHECK` pins it again.
 *   - `code`             — generated by the table's trigger; immutable.
 *   - `transcription_status` / `summary_status` — SERVER-OWNED (Clay's ruling
 *                          1, 2026-09-24): the column default on create, the
 *                          Hono transcribe/summarize routes' CAS, and the edge
 *                          functions. Never a client write.
 *   - `transcription_*` / `summary_*` — written by the edge functions only.
 *   - `audio_file_path` on UPDATE — fixed at create; a memo cannot be
 *                          re-pointed at another object (the unique index
 *                          stops two memos sharing one, this stops the swap).
 *
 * Two things the DB does NOT guard are checked in the handler, not here: the
 * audio path's first segment must equal `:orgId`, and `project_id` /
 * `business_id` / `person_id` must be rows in the org (the FKs ignore org).
 */
import { z } from "zod";
/**
 * The only audio object path a memo may carry: `<org uuid>/memos/<name>.<ext>`.
 *
 * A WHOLE-STRING match, so it rules out by construction every traversal
 * spelling the service-role download would otherwise honour: no dot-segments
 * (`.` is not in the name class), no percent-encoding (`%` is not either), no
 * extra `/` and no other extension. The name class admits both the current
 * `<uuid>.webm` and the legacy `<memo id>-<epoch ms>.webm` spelling.
 *
 * It does NOT prove the first segment is the caller's org — the API and the
 * `memo-transcribe` edge function compare it to the org id themselves. The
 * edge function keeps a verbatim copy (Deno cannot import `@releve/shared`);
 * change both together.
 */
export const MEMO_AUDIO_PATH_RE = /^[0-9a-f-]{36}\/memos\/[A-Za-z0-9_-]+\.(webm|mp4)$/;
/** The two containers the recorder produces (`MediaRecorder` mime, params stripped). */
export const MEMO_AUDIO_CONTENT_TYPES = ["audio/webm", "audio/mp4"];
/** Four hours — the sanity ceiling on a recording's reported length. */
export const MEMO_MAX_DURATION_SECONDS = 4 * 60 * 60;
/** Max entries in `speaker_assignments` (one per diarised speaker). */
export const MEMO_MAX_SPEAKER_ASSIGNMENTS = 20;
/**
 * The `memos.transcription_status` CHECK's values
 * (`20260211164852_create_memos.sql`). Column default `pending`.
 */
export const MEMO_TRANSCRIPTION_STATUSES = [
    "pending",
    "processing",
    "completed",
    "failed",
];
/**
 * The `memos.summary_status` CHECK's values
 * (`20260217094404_add_memo_summary.sql`). Column default `none` — no summary
 * has been asked for.
 */
export const MEMO_SUMMARY_STATUSES = [
    "none",
    "pending",
    "processing",
    "completed",
    "failed",
];
const nullableUuid = z.string().uuid().nullable().optional();
/** Fields a create and an update share. */
const memoLinkFields = {
    description: z.string().max(10000).nullable().optional(),
    project_id: nullableUuid,
    business_id: nullableUuid,
    /**
     * A `people` id (FK `memos_person_id_fkey`, ON DELETE SET NULL). Org
     * membership is checked in the handler and by the `memos_validate_org`
     * guard trigger.
     */
    person_id: nullableUuid,
};
const memoTitle = z.string().trim().min(1).max(200);
/**
 * POST /orgs/:orgId/memos body. The browser uploads the audio first, then
 * posts its path.
 *
 * `enable_entity_detection` is NOT a column — the handler strips it off and
 * forwards it to the `memo-transcribe` invoke.
 *
 * `audio_duration_seconds` may be 0: the recorder counts whole seconds, so a
 * recording stopped inside its first second reports 0.
 */
export const createMemoSchema = z
    .object({
    title: memoTitle,
    ...memoLinkFields,
    audio_file_path: z.string().regex(MEMO_AUDIO_PATH_RE, "invalid audio path"),
    audio_duration_seconds: z.number().finite().min(0).max(MEMO_MAX_DURATION_SECONDS),
    audio_content_type: z.enum(MEMO_AUDIO_CONTENT_TYPES),
    enable_entity_detection: z.boolean().optional(),
})
    .strict();
/**
 * `speaker_id → org member user id`. Shape only: the values are display
 * lookups (a removed member renders "Unknown member"), not FKs, so the
 * handler does not check them against the org.
 */
export const memoSpeakerAssignmentsSchema = z
    .record(z.string().regex(/^speaker_\d+$/), z.string().uuid())
    .refine((v) => Object.keys(v).length <= MEMO_MAX_SPEAKER_ASSIGNMENTS, {
    message: `At most ${MEMO_MAX_SPEAKER_ASSIGNMENTS} speaker assignments`,
});
/**
 * PATCH /orgs/:orgId/memos/:memoId body — any subset, at least one key.
 * `title` stays non-nullable (NOT NULL column). `speaker_assignments` replaces
 * the whole map (NULL clears it).
 */
export const updateMemoSchema = z
    .object({
    title: memoTitle.optional(),
    ...memoLinkFields,
    speaker_assignments: memoSpeakerAssignmentsSchema.nullable().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field must be provided",
});
/** POST /orgs/:orgId/memos/:memoId/transcribe body. */
export const transcribeMemoSchema = z
    .object({
    enable_entity_detection: z.boolean(),
})
    .strict();
//# sourceMappingURL=memos.js.map